// Shift-owner "clock-in" helper — used when a device is running in kiosk
// mode as a shared underground field terminal. The supervisor picks the
// worker that is physically operating this device for the shift, and every
// HSE capture done from the device gets stamped with their name/id.
//
// Storage: localStorage (persists across offline reloads without needing
// IndexedDB migrations, since the payload is tiny).
const KEY = "shift-owner-v1";

const listeners = new Set();

function notify() {
  const value = get();
  listeners.forEach((cb) => { try { cb(value); } catch (_e) { /* noop */ } });
}

/** Returns `{id, name, position?, area?, crew?, started_at}` or null. */
export function get() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.name) return null;
    return parsed;
  } catch (_err) {
    return null;
  }
}

/** Set the current shift owner. `employee` must have {id, name}. */
export function set(employee) {
  if (!employee?.id || !employee?.name) return;
  const payload = {
    id: employee.id,
    name: employee.name,
    position: employee.position || "",
    area: employee.area || "",
    crew: employee.crew || "",
    started_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(payload));
    notify();
  } catch (err) {
    console.warn("shiftOwner.set failed:", err?.message || err);
  }
}

/** Clear the shift owner — called when the supervisor ends the shift or
 *  exits kiosk mode. */
export function clear() {
  try {
    localStorage.removeItem(KEY);
    notify();
  } catch (err) {
    console.warn("shiftOwner.clear failed:", err?.message || err);
  }
}

/** Subscribe to shift-owner changes. Calls back with the current value. */
export function subscribe(cb) {
  listeners.add(cb);
  try { cb(get()); } catch (_e) { /* noop */ }
  return () => listeners.delete(cb);
}

/** Endpoints that should carry the shift owner stamp in POST/PUT bodies.
 *  Kept explicit to avoid leaking the owner into unrelated modules. */
const STAMP_MUTATION_URLS = [
  /\/hse\/observations$/,
  /\/hse\/observations\/[^/]+$/,
  /\/hse\/toolbox$/,
  /\/hse\/toolbox\/[^/]+$/,
  /\/hse\/investigations$/,
  /\/hse\/investigations\/[^/]+$/,
];

/** Inject `shift_owner_{id,name}` into the payload if the request targets
 *  an HSE mutation and a shift owner is active. Idempotent — never over-
 *  writes an explicit value the caller may have already set. */
export function stampPayload(config) {
  const owner = get();
  if (!owner) return config;
  const url = config?.url || "";
  const method = (config?.method || "").toLowerCase();
  if (!(method === "post" || method === "put")) return config;
  if (!STAMP_MUTATION_URLS.some((rx) => rx.test(url))) return config;
  let data = config.data;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (_e) { return config; }
  }
  if (!data || typeof data !== "object") return config;
  if (!data.shift_owner_id) data.shift_owner_id = owner.id;
  if (!data.shift_owner_name) data.shift_owner_name = owner.name;
  config.data = data;
  return config;
}
